import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './StatusPill.module.css';

/**
 * StatusPill — one of six fixed record states.
 *
 * Handoff, Accessibility: "Status is never colour alone: dot or glyph **plus
 * the word**, in both locales." So the word is not optional and cannot be
 * turned off — there is no `iconOnly` prop and no way to render an empty pill.
 *
 * This is the one primitive that reads a string it was not given. Button's rule
 * ("the component renders no strings") exists so a primitive cannot ship
 * English to an Arabic agent; here the opposite applies. The six statuses are a
 * closed vocabulary that the design system owns — the handoff prints both
 * halves of every pair itself ("Open / مفتوحة") — and leaving the words to the
 * caller is how the same status ends up spelled three ways across six screens.
 * The words come from the i18n layer, so both locales are covered; `label` is
 * available for the rare caller that must override.
 */

export type Status = 'open' | 'in-progress' | 'waiting' | 'escalated' | 'closed' | 'cancelled';

/**
 * Status name -> [translation key, CSS module class].
 *
 * `as const` is load-bearing: task 19 typed the i18n resources, so `t()` only
 * accepts a key that exists in en.json. A `Record<Status, [string, string]>`
 * would widen these to `string` and hand that guarantee back.
 */
const STATUS = {
  open: ['status.open', 'open'],
  'in-progress': ['status.inProgress', 'inProgress'],
  waiting: ['status.waiting', 'waiting'],
  escalated: ['status.escalated', 'escalated'],
  closed: ['status.closed', 'closed'],
  cancelled: ['status.cancelled', 'cancelled'],
} as const satisfies Record<Status, readonly [string, string]>;

export interface StatusPillProps {
  status: Status;
  /** Overrides the built-in word. The pill is never wordless either way. */
  label?: string;
  className?: string;
}

export const StatusPill = forwardRef<HTMLSpanElement, StatusPillProps>(function StatusPill(
  { status, label, className },
  ref,
) {
  const { t } = useTranslation();
  const [key, variant] = STATUS[status];

  return (
    <span
      ref={ref}
      className={[styles['pill'], styles[variant], className].filter(Boolean).join(' ')}
      data-status={status}
    >
      {/*
        Decorative. The dot repeats what the word already says, which is the
        point — it is the redundancy that makes the pill readable without
        colour. Announced, it would read as an unnamed image.
      */}
      <span className={styles['dot']} aria-hidden="true" data-testid="statusDot" />
      {label ?? t(key)}
    </span>
  );
});
